/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { genId } from "@smartsheet/lodestar-utils";
import { LogActionId } from "@smartsheet/logging-state-service";
import fuzzySort from "fuzzysort";
import { Ref, useCallback, useMemo } from "react";
import { I18nContextProvider } from "../../i18n/i18n";
import { useLoggingDecorator, useLoggingEnabledByDefault } from "../../util/logging";
import { MultiSelectBasic } from "./components/multiselect/MultiSelectBasic";
import { MultiSelectComboBox } from "./components/multiselect/MultiSelectComboBox";
import { FUZZYSORT_MIN_THRESHOLD, DEFAULT_MAX_MENU_HEIGHT } from "./constants";
import { injectCatalog } from "./i18n/SelectCatalog";
import { BaseOptionType, MultiSelectProps } from "./types";
import { internalOptionToString, isSelectV2OptionSeparator, selectAllMarkerOption } from "./utils";

// TODO it'd be really nice to get this HTMLElement generic a bit cleaner for consumers.
//A Type union for all things related to focus and refs might fit nicely.
export const MultiSelect = <Option extends BaseOptionType>({
    clientId,
    enableSelectAll = true,
    innerRef,
    isOptionDisabled,
    isLoading = false,
    isReadonly = false,
    isClearable = false,
    isSearchable,
    locale,
    maxMenuHeight = DEFAULT_MAX_MENU_HEIGHT,
    maxOptionsUntilVirtualization = 50,
    minHeight,
    maxSelectedRows = 3,
    rollupSelectionAfter = 50,
    onChange,
    optionHeight,
    useFuzzySearch = false,
    options: providedOptions,
    optionToString = internalOptionToString,
    menuId: providedMenuId,
    filterOption: providedFilterOption,
    inputValue,
    setInputValue,
    hideDropdownArrow = false,
    placeholder,
    overrides,
    ...rest
}: MultiSelectProps<Option, HTMLButtonElement | HTMLInputElement>) => {
    if (process.env.NODE_ENV !== "production") {
        const hiddenInputNameDefined = typeof rest.hiddenInputName === "string";
        const getHiddenInputValueDefined = typeof rest.getHiddenInputValue === "function";
        if (hiddenInputNameDefined !== getHiddenInputValueDefined) {
            console.warn(
                "MultiSelect: To use the hidden input, you must include both" +
                    " props: getHiddenInputValue and hiddenInputName"
            );
        }
    }

    injectCatalog();

    const onChangeWithLogging = useLoggingEnabledByDefault(
        useLoggingDecorator({
            controlType: "select",
            clientId,
        }),
        { actionId: LogActionId.CLICK },
        onChange
    );

    const defaultFilterOption = useCallback(
        (opt: Option, input: string, fuzzySearchScoreMap?: Map<Option, number>) => {
            if (useFuzzySearch) {
                const result = fuzzySort.single(input, optionToString(opt));
                if (result) {
                    if (fuzzySearchScoreMap) {
                        fuzzySearchScoreMap.set(opt, result.score);
                    }
                    return result.score >= FUZZYSORT_MIN_THRESHOLD;
                }
                return false;
            } else {
                return optionToString(opt)?.toLowerCase().startsWith(input);
            }
        },
        [optionToString, useFuzzySearch]
    );

    const memoizedOptions = useMemo<Option[]>(() => {
        // Don't enable select all if there are no options.
        if (enableSelectAll && providedOptions.length > 0) {
            return [selectAllMarkerOption as unknown as Option, ...providedOptions];
        } else {
            return providedOptions;
        }
    }, [enableSelectAll, providedOptions]);

    // Filters out all non-selectable options from being selected with `selectAll`
    const selectableOptions = useMemo<Option[]>(() => {
        if (enableSelectAll && providedOptions.length > 0) {
            return providedOptions.filter(
                (option) => !isOptionDisabled?.(option) && !isSelectV2OptionSeparator(option)
            );
        } else {
            return providedOptions;
        }
    }, [enableSelectAll, isOptionDisabled, providedOptions]);

    const menuId = useMemo(() => providedMenuId || genId("select-menu"), [providedMenuId]);

    const sharedProps = {
        memoizedOptions: memoizedOptions,
        onChange: onChangeWithLogging,
        options: selectableOptions,
        clientId,
        isOptionDisabled,
        enableSelectAll,
        isClearable,
        isLoading,
        isReadonly,
        isSearchable,
        locale,
        maxMenuHeight,
        maxOptionsUntilVirtualization,
        minHeight,
        maxSelectedRows,
        rollupSelectionAfter,
        optionHeight,
        optionToString,
        menuId,
        filterOption: providedFilterOption || defaultFilterOption,
        hideDropdownArrow,
        overrides,
    };

    const isComboBox = isSearchable || typeof rest.onCreateOption === "function";

    return (
        <I18nContextProvider locale={locale}>
            {isComboBox ? (
                <MultiSelectComboBox
                    {...rest}
                    {...sharedProps}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    innerRef={innerRef as Ref<HTMLInputElement>}
                    placeholder={placeholder}
                    useFuzzySearch={useFuzzySearch}
                />
            ) : (
                <MultiSelectBasic
                    {...rest}
                    {...sharedProps}
                    innerRef={innerRef as Ref<HTMLButtonElement>}
                    placeholder={placeholder}
                />
            )}
        </I18nContextProvider>
    );
};

MultiSelect.displayName = "MultiSelect";
